import type { CardLink, Json } from '@/types/database';

/**
 * Products are card_links rows inside a `products` section:
 *   type        'product'
 *   label       product name
 *   value       image URL (may be empty)
 *   description longer blurb (shown in full on the detail sheet)
 *   metadata    ProductMeta — price, stock, visibility, badges, bullets, options
 *
 * Reusing card_links means products get drag-reorder, template snapshots,
 * duplicate-card, and the editor preview for free.
 */

export type ProductOptionType = 'dropdown' | 'buttons';

export interface ProductOption {
  id: string;
  /** "Size", "Color", "Heat"… */
  name: string;
  type: ProductOptionType;
  values: string[];
}

export interface ProductMeta {
  price_cents: number;
  currency: string;
  /** In stock. Sold-out products still show, greyed. */
  available: boolean;
  /** Visible to visitors. New products start hidden so drafts don't leak. */
  published: boolean;
  /** Up to two short labels shown on the photo ("HOT", "Large", "New"). */
  badges: string[];
  /** Bullet points on the detail sheet. */
  bullets: string[];
  /** Selectable options; every option must be chosen before adding. */
  options: ProductOption[];
}

export interface Product {
  id: string;
  name: string;
  imageUrl: string | null;
  description: string | null;
  priceCents: number;
  currency: string;
  available: boolean;
  published: boolean;
  badges: string[];
  bullets: string[];
  options: ProductOption[];
}

export const MAX_BADGES = 2;
export const MAX_BADGE_LENGTH = 14;
export const MAX_OPTIONS = 6;
export const MAX_OPTION_VALUES = 20;

/** What a brand-new product row gets. Hidden until the owner switches it on. */
export const DEFAULT_PRODUCT_META: ProductMeta = {
  price_cents: 0,
  currency: 'USD',
  available: true,
  published: false,
  badges: [],
  bullets: [],
  options: [],
};

function strArray(v: unknown, maxLen: number, maxItems: number): string[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((x): x is string => typeof x === 'string')
    .map((x) => x.trim().slice(0, maxLen))
    .filter(Boolean)
    .slice(0, maxItems);
}

function parseOptions(v: unknown): ProductOption[] {
  if (!Array.isArray(v)) return [];
  const out: ProductOption[] = [];
  for (const raw of v) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const o = raw as Record<string, unknown>;
    const name = typeof o.name === 'string' ? o.name.trim().slice(0, 40) : '';
    const values = strArray(o.values, 40, MAX_OPTION_VALUES);
    if (!name) continue;
    out.push({
      id: typeof o.id === 'string' && o.id ? o.id : `opt-${out.length}`,
      name,
      type: o.type === 'buttons' ? 'buttons' : 'dropdown',
      values,
    });
    if (out.length >= MAX_OPTIONS) break;
  }
  return out;
}

export function productMeta(metadata: Json | null | undefined): ProductMeta {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    // A row with no metadata at all predates the shop fields — treat it as
    // visible so nothing that was showing disappears.
    return { ...DEFAULT_PRODUCT_META, published: true };
  }
  const m = metadata as Record<string, unknown>;
  const price =
    typeof m.price_cents === 'number' && Number.isFinite(m.price_cents)
      ? Math.max(0, Math.round(m.price_cents))
      : 0;
  return {
    price_cents: price,
    currency: typeof m.currency === 'string' && m.currency ? m.currency : 'USD',
    available: m.available !== false,
    // Missing key = legacy product created before visibility existed → visible.
    published: m.published !== false,
    badges: strArray(m.badges, MAX_BADGE_LENGTH, MAX_BADGES),
    bullets: strArray(m.bullets, 160, 12),
    options: parseOptions(m.options),
  };
}

export function productFromLink(link: CardLink): Product {
  const meta = productMeta(link.metadata);
  return {
    id: link.id,
    name: link.label?.trim() || 'Untitled product',
    imageUrl: link.value?.trim() ? link.value.trim() : null,
    description: link.description?.trim() ? link.description.trim() : null,
    priceCents: meta.price_cents,
    currency: meta.currency,
    available: meta.available,
    published: meta.published,
    badges: meta.badges,
    bullets: meta.bullets,
    options: meta.options,
  };
}

/** Merge a partial patch into the link's metadata, keeping unknown keys. */
export function withProductMeta(link: CardLink, patch: Partial<ProductMeta>): Json {
  const base =
    link.metadata && typeof link.metadata === 'object' && !Array.isArray(link.metadata)
      ? (link.metadata as Record<string, Json | undefined>)
      : {};
  const current = productMeta(link.metadata);
  return { ...base, ...current, ...patch } as unknown as Json;
}

export function newOptionId(): string {
  return `opt-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Cart ────────────────────────────────────────────────────────────────────
/** Chosen option values keyed by option name: { Size: 'Large', Heat: 'Hot' }. */
export type SelectedOptions = Record<string, string>;

export interface CartLine {
  /** product id + canonical options — one line per distinct variant. */
  key: string;
  product: Product;
  qty: number;
  options: SelectedOptions;
}

/** Stable key so "Large / Hot" and "Hot / Large" collapse to one line. */
export function lineKey(productId: string, options: SelectedOptions | undefined): string {
  const entries = Object.entries(options ?? {})
    .filter(([, v]) => v)
    .sort(([a], [b]) => a.localeCompare(b));
  return entries.length === 0 ? productId : `${productId}|${JSON.stringify(entries)}`;
}

/** "Size: Large · Heat: Hot" — for cart lines, emails, the orders drawer. */
export function formatOptions(options: SelectedOptions | null | undefined): string {
  if (!options) return '';
  return Object.entries(options)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join(' · ');
}

/** True when every option on the product has a chosen value. */
export function optionsComplete(product: Product, selected: SelectedOptions): boolean {
  return product.options.every((o) => o.values.length === 0 || !!selected[o.name]);
}

export function cartSubtotal(lines: CartLine[]): number {
  return lines.reduce((s, l) => s + l.product.priceCents * l.qty, 0);
}

export function cartCount(lines: CartLine[]): number {
  return lines.reduce((s, l) => s + l.qty, 0);
}
